<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { VARIABLE_CATEGORIES } from './variableCatalog';

const emit = defineEmits(['insert']);
const { t } = useI18n();

const search = ref('');
const openKeys = ref(new Set(VARIABLE_CATEGORIES.map(c => c.key)));

const filteredCategories = computed(() => {
  const term = search.value.trim().toLowerCase();
  return VARIABLE_CATEGORIES.map(category => {
    const variables = term
      ? category.variables.filter(
          v =>
            v.key.toLowerCase().includes(term) ||
            v.label.toLowerCase().includes(term)
        )
      : category.variables;
    return { ...category, variables };
  }).filter(category => category.variables.length > 0);
});

function toggle(key) {
  const next = new Set(openKeys.value);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  openKeys.value = next;
}

const isOpen = key => Boolean(search.value) || openKeys.value.has(key);

// Built in JS so the literal braces never reach the Vue template compiler.
const placeholderText = key => `{{ ${key} }}`;
</script>

<template>
  <div class="flex flex-col h-full">
    <div class="p-3 border-b border-n-weak">
      <div class="relative">
        <span
          class="absolute -translate-y-1/2 i-lucide-search size-4 left-2.5 top-1/2 text-n-slate-10"
        />
        <input
          v-model="search"
          type="text"
          :placeholder="t('DOCUMENT_TEMPLATES.EDITOR.VARIABLES.SEARCH')"
          class="py-1.5 pr-2 pl-8 w-full text-sm rounded-md border border-n-weak bg-n-alpha-black2 text-n-slate-12"
        />
      </div>
    </div>

    <div class="overflow-y-auto flex-1 p-2">
      <div
        v-for="category in filteredCategories"
        :key="category.key"
        class="mb-1"
      >
        <button
          class="flex gap-2 items-center px-2 py-2 w-full text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-2"
          @click="toggle(category.key)"
        >
          <span :class="category.icon" class="size-4 text-n-slate-11" />
          <span class="font-medium">{{ category.label }}</span>
          <span
            class="px-1.5 ml-auto text-xs rounded-full bg-n-slate-3 text-n-slate-11"
          >
            {{ category.variables.length }}
          </span>
          <span
            class="size-4 text-n-slate-10"
            :class="
              isOpen(category.key)
                ? 'i-lucide-chevron-down'
                : 'i-lucide-chevron-right'
            "
          />
        </button>

        <div v-if="isOpen(category.key)" class="mt-0.5 space-y-0.5">
          <button
            v-for="variable in category.variables"
            :key="variable.key"
            class="px-2 py-1.5 w-full text-left rounded-md group hover:bg-n-alpha-2"
            @click="emit('insert', variable.key, variable.label)"
          >
            <div class="flex justify-between items-center">
              <span class="text-sm font-medium text-n-slate-12">
                {{ variable.label }}
              </span>
              <span
                class="opacity-0 transition-opacity i-lucide-plus size-3.5 text-n-slate-11 group-hover:opacity-100"
              />
            </div>
            <p class="font-mono text-xs text-n-slate-10">
              {{ placeholderText(variable.key) }}
            </p>
            <p v-if="variable.example" class="text-xs text-n-slate-10">
              {{ t('DOCUMENT_TEMPLATES.EDITOR.VARIABLES.EXAMPLE') }}:
              {{ variable.example }}
            </p>
          </button>
        </div>
      </div>

      <p
        v-if="!filteredCategories.length"
        class="py-6 text-sm text-center text-n-slate-10"
      >
        {{ t('DOCUMENT_TEMPLATES.EDITOR.VARIABLES.EMPTY') }}
      </p>
    </div>
  </div>
</template>
